import React, {
  useEffect, useState, useMemo, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { observer } from 'mobx-react';

import { Box, Chip, Typography } from '@mui/material';
import MealPlanCreationRequest from '../../../../Model/MealPlanCreationRequest';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import {
  Container, MetaTable, Overlay, RecipeGrid,
} from './styles';

function GenerationStatus({ requestId, onMealPlanGenerationComplete }) {
  const isComponentMountedRef = useComponentMounted();
  const [mealPlanCreationRequestDoc, setMealPlanCreationRequestDoc] = useState();

  const { recipes, images, status } = mealPlanCreationRequestDoc ?? {};

  useEffect(() => {
    const fetchMealPlanCreationRequest = async () => {
      const mealPlanCreationRequest = await MealPlanCreationRequest.getMealPlanCreationRequest(requestId);

      if (isComponentMountedRef.current) {
        setMealPlanCreationRequestDoc(mealPlanCreationRequest);
      }
    };
    fetchMealPlanCreationRequest();
  }, [isComponentMountedRef, requestId]);

  useEffect(() => {
    if (status === 'COMPLETE') {
      onMealPlanGenerationComplete(requestId);
    }
  }, [requestId, status, onMealPlanGenerationComplete]);

  const recipesByMealType = useMemo(() => {
    if (!recipes) {
      return new Map();
    }

    return recipes.reduce((acc, recipe) => {
      if (!acc.has(recipe.mealType)) {
        acc.set(recipe.mealType, []);
      }
      acc.get(recipe.mealType).push(recipe);
      return acc;
    }, new Map());
  }, [recipes]);

  const imagesByRecipeId = useMemo(() => {
    if (!images) {
      return new Map();
    }

    return images.reduce((acc, image) => {
      acc.set(image.recipeId, image);
      return acc;
    }, new Map());
  }, [images]);

  return (
    <Overlay>
      <Container>
        <Typography variant="h5" component="h5" mb={2}>
          AI Meal Plan Generation
        </Typography>
        {mealPlanCreationRequestDoc && (
          <>
            <MetaTable>
              <tbody>
                <tr>
                  <th>Request ID</th>
                  <td>{requestId}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <Chip
                      label={mealPlanCreationRequestDoc.status}
                      color={mealPlanCreationRequestDoc.status === 'COMPLETE' ? 'success' : 'default'}
                      size="small"
                      sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                    />
                  </td>
                </tr>
              </tbody>
            </MetaTable>

            <Box mt={2}>
              {mealPlanCreationRequestDoc.status === 'REQUESTED' && (
              <Typography variant="body1" component="div">
                AI Meal Plan Generation request has been received.
              </Typography>
              )}
              {mealPlanCreationRequestDoc.status === 'RECIPES_GENERATING' && (
              <Typography variant="body1" component="div">
                Recipes are being generated. Please wait...
              </Typography>
              )}
              {!['REQUESTED', 'RECIPES_GENERATING'].includes(mealPlanCreationRequestDoc.status) && (
                <Typography variant="h6" component="h6" mb={1}>
                  Generated Recipes
                </Typography>
              )}

              {Array.from(recipesByMealType.entries()).map(([mealType, generatedRecipes]) => (
                <Box key={mealType} mb={2}>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    fontWeight="bold"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {mealType}
                  </Typography>
                  <RecipeGrid>
                    {generatedRecipes.map((recipe) => (
                      <Fragment key={recipe.id}>
                        <Typography variant="body2">{recipe.name}</Typography>
                        <Box>
                          {imagesByRecipeId.get(recipe.id)?.imageUrl ? (
                            <Chip label="Image Generated" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Pending Image Generation" size="small" color="default" variant="outlined" />
                          )}
                        </Box>
                      </Fragment>
                    ))}
                  </RecipeGrid>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Container>
    </Overlay>
  );
}

GenerationStatus.propTypes = {
  requestId: PropTypes.string.isRequired,
  onMealPlanGenerationComplete: PropTypes.func,
};

GenerationStatus.defaultProps = {
  onMealPlanGenerationComplete: () => {},
};

export default compose(observer)(GenerationStatus);
